import { useState } from "react";
import { Link } from "react-router-dom";
import { Award, CheckCircle2, Download, LifeBuoy } from "lucide-react";
import { toast } from "sonner";
import Reveal from "@/components/Reveal";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useLang } from "@/i18n";

const CATEGORIES = [
  "ICT Support",
  "Network & Internet",
  "Cybersecurity",
  "Software & Systems",
  "Hardware & Repair",
  "Cloud & Email",
  "Other",
];
const PRIORITIES = ["low", "normal", "high", "urgent"];

const EMPTY = { name: "", email: "", phone: "", company: "", category: "ICT Support", priority: "normal", subject: "", message: "" };

export default function Support() {
  const [form, setForm] = useState(EMPTY);
  const [sending, setSending] = useState(false);
  const [ticket, setTicket] = useState(null);
  const [certRef, setCertRef] = useState("");
  const [certEmail, setCertEmail] = useState("");
  const [certState, setCertState] = useState(null); // null | "searching" | "done" | "not_found" | "not_ready"
  const { t } = useLang();

  const downloadCertificate = async (e) => {
    e.preventDefault();
    setCertState("searching");
    try {
      const res = await api.get(`/certificates/${encodeURIComponent(certRef.trim())}`, {
        params: { email: certEmail.trim() },
        responseType: "blob",
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `KINOS-Certificate-${certRef.trim().toUpperCase()}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      setCertState("done");
    } catch (err) {
      const status = err.response?.status;
      setCertState(status === 409 ? "not_ready" : "not_found");
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const { data } = await api.post("/support", form);
      setTicket(data);
      setForm(EMPTY);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  const field =
    "bg-[#0B1120] border-white/10 text-slate-100 placeholder:text-slate-600 focus-visible:ring-[#ED1C24] rounded-none h-12";
  const selectCls = `${field} w-full`;

  return (
    <main data-testid="support-page" className="min-h-screen bg-[#0B1120] px-6 md:px-12 lg:px-24 pt-32 pb-24">
      <Reveal y={24}>
        <p className="text-xs tracking-[0.25em] uppercase text-[#ED1C24] mb-4">{t("support_label")}</p>
        <h1 className="font-display font-semibold text-white text-4xl sm:text-6xl tracking-tight mb-4">
          {t("support_title").replace(".", "")}<span className="text-[#FFC61A]">.</span>
        </h1>
        <p className="text-slate-400 max-w-2xl mb-14">{t("support_sub")}</p>
      </Reveal>

      <div className="max-w-3xl">
        {ticket ? (
          <Reveal>
            <div data-testid="ticket-success-panel" className="bg-[#0F172A] border border-white/10 p-10 text-center">
              <CheckCircle2 size={52} className="text-[#FFC61A] mx-auto mb-6" />
              <h2 className="font-display font-semibold text-white text-3xl mb-3">
                {t("sup_success").replace(".", "")}<span className="text-[#ED1C24]">.</span>
              </h2>
              <p className="text-slate-400 text-sm mb-2">{t("sup_ticket_number")}</p>
              <p data-testid="ticket-number" className="font-display text-2xl text-[#FFC61A] mb-6">
                {ticket.ticket_number}
              </p>
              <p className="text-slate-400 text-sm leading-relaxed mb-8">{t("sup_success_note")}</p>
              <button
                data-testid="new-ticket-btn"
                onClick={() => setTicket(null)}
                className="bg-[#ED1C24] hover:bg-[#C81016] text-white font-medium px-8 py-3.5 transition-colors duration-300"
              >
                {t("sup_new")}
              </button>
            </div>
          </Reveal>
        ) : (
          <Reveal delay={0.1}>
            <form
              data-testid="support-form"
              onSubmit={submit}
              className="bg-[#0F172A] border border-white/10 p-8 lg:p-10 space-y-5"
            >
              <div className="grid sm:grid-cols-2 gap-5">
                <Input data-testid="support-name-input" required placeholder={t("ph_name")} value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })} className={field} />
                <Input data-testid="support-email-input" required type="email" placeholder={t("ph_email")} value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })} className={field} />
                <Input data-testid="support-phone-input" placeholder={t("ph_phone")} value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })} className={field} />
                <Input data-testid="support-company-input" placeholder={t("ph_company")} value={form.company}
                  onChange={(e) => setForm({ ...form, company: e.target.value })} className={field} />
                <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                  <SelectTrigger data-testid="support-category-select" className={selectCls}>
                    <SelectValue placeholder={t("sup_category")} />
                  </SelectTrigger>
                  <SelectContent className="bg-[#0F172A] text-slate-100 border-white/10 rounded-none">
                    {CATEGORIES.map((c) => (
                      <SelectItem key={c} value={c} data-testid={`support-cat-${c.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>{c}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                  <SelectTrigger data-testid="support-priority-select" className={selectCls}>
                    <SelectValue placeholder={t("sup_priority")} />
                  </SelectTrigger>
                  <SelectContent className="bg-[#0F172A] text-slate-100 border-white/10 rounded-none">
                    {PRIORITIES.map((p) => (
                      <SelectItem key={p} value={p} data-testid={`support-pri-${p}`}>{t(`pri_${p}`)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Input data-testid="support-subject-input" required placeholder={t("contact_ph_subject")} value={form.subject}
                onChange={(e) => setForm({ ...form, subject: e.target.value })} className={field} />
              <Textarea data-testid="support-message-input" required rows={5} placeholder={t("sup_message")} value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                className="bg-[#0B1120] border-white/10 text-slate-100 placeholder:text-slate-600 focus-visible:ring-[#ED1C24] rounded-none" />
              <button
                data-testid="support-submit-btn"
                type="submit"
                disabled={sending}
                className="w-full inline-flex items-center justify-center gap-3 bg-[#ED1C24] hover:bg-[#C81016] disabled:opacity-60 text-white font-medium py-4 transition-all duration-300 hover:shadow-[0_0_30px_rgba(237,28,36,0.4)]"
              >
                <LifeBuoy size={17} />
                {sending ? t("sup_sending") : t("sup_submit")}
              </button>
            </form>
          </Reveal>
        )}
      </div>

      <Reveal delay={0.15}>
        <div data-testid="certificate-section" className="max-w-3xl mt-20 border-t border-white/10 pt-14">
          <div className="flex items-center gap-3 mb-3">
            <Award size={20} className="text-[#FFC61A]" />
            <p className="text-xs tracking-[0.25em] uppercase text-[#ED1C24]">{t("cert_label")}</p>
          </div>
          <h2 className="font-display font-semibold text-white text-3xl sm:text-4xl tracking-tight mb-3">
            {t("cert_title").replace(".", "")}<span className="text-[#FFC61A]">.</span>
          </h2>
          <p className="text-slate-400 text-sm mb-8 max-w-2xl">{t("cert_sub")}</p>

          <form
            data-testid="certificate-form"
            onSubmit={downloadCertificate}
            className="bg-[#0F172A] border border-white/10 p-8 lg:p-10 space-y-5"
          >
            <div className="grid sm:grid-cols-2 gap-5">
              <Input
                data-testid="cert-ref-input"
                required
                placeholder={t("cert_ph_ref")}
                value={certRef}
                onChange={(e) => { setCertRef(e.target.value); setCertState(null); }}
                className={field}
              />
              <Input
                data-testid="cert-email-input"
                required
                type="email"
                placeholder={t("cert_ph_email")}
                value={certEmail}
                onChange={(e) => { setCertEmail(e.target.value); setCertState(null); }}
                className={field}
              />
            </div>
            {certState === "not_found" && (
              <p data-testid="cert-error-not-found" className="text-sm text-[#ED1C24] border-l-2 border-[#ED1C24] pl-3">
                {t("cert_not_found")}
              </p>
            )}
            {certState === "not_ready" && (
              <p data-testid="cert-error-not-ready" className="text-sm text-[#FFC61A] border-l-2 border-[#FFC61A] pl-3">
                {t("cert_not_ready")}
              </p>
            )}
            {certState === "done" && (
              <p data-testid="cert-success" className="text-sm text-emerald-400 border-l-2 border-emerald-400 pl-3">
                {t("cert_done")}
              </p>
            )}
            <button
              data-testid="cert-download-btn"
              type="submit"
              disabled={certState === "searching"}
              className="w-full inline-flex items-center justify-center gap-3 bg-[#FFC61A] hover:bg-[#e6b118] disabled:opacity-60 text-[#0B1120] font-semibold py-4 transition-all duration-300"
            >
              <Download size={17} />
              {certState === "searching" ? t("cert_searching") : t("cert_btn")}
            </button>
          </form>
        </div>
      </Reveal>
    </main>
  );
}
